import { getTransactionStatementsThunk } from '@redux/features/transaction-statements.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import Pagination from 'Common/Pagination';
import React, { FC, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const TransactionStatements: FC = () => {
    // -----------------------------------------------------------------
    const [page, setPage] = useState<number>(1);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    // -----------------------------------------------------------------

    const dispatch = useAppDispatch();
    const { transactionStatements, loading } = useAppSelector((state: RootState) => state.transactionStatementsState);
    // -----------------------------------------------------------------

    const changePage = (p: string) => setPage(Number(p));
    // -----------------------------------------------------------------

    // Helper function to format date to YYYY-MM-DDTHH:MM:SS format
    const formatDateForAPI = (date: Date, isEndDate: boolean = false): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        // Start date: 00:00:00, End date: 23:59:59 to cover entire day
        const time = isEndDate ? '23:59:59' : '00:00:00';
        return `${year}-${month}-${day}T${time}`;
    };

    // Handle date range changes - only send when both dates are selected
    const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
        setDateRange(dates);
        const [start, end] = dates;
        // Only update string dates when both start and end are selected
        if (start && end) {
            setStartDate(formatDateForAPI(start, false));
            setEndDate(formatDateForAPI(end, true));
        } else if (!start && !end) {
            // Clear both when range is cleared
            setStartDate('');
            setEndDate('');
        }
        // If only start is selected, don't update the string dates yet
    };

    // -----------------------------------------------------------------

    useEffect(() => {
        dispatch(
            getTransactionStatementsThunk({
                size: 10,
                page: page - 1,
                serialNumber: '',
                ndcCode: '',
                fromDuns: '',
                toDuns: '',
                startDate: startDate || '',
                endDate: endDate || '',
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, startDate, endDate]);
    // -----------------------------------------------------------------

    // Helper function to format date
    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    // Helper function to format boolean
    const formatBoolean = (value: boolean | null | undefined) => {
        if (value === null || value === undefined) return '-';
        return value ? 'Yes' : 'No';
    };

    // -----------------------------------------------------------------

    return (
        <>
            <BreadCrumb pageTitle={`Transaction Statements`} />
            <section className="py-5">
                <div className=" p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">Transaction Statements List</h3>
                    </div>
                    <div className="flex flex-col gap-4">
                        <div className="flex gap-5 flex-wrap">
                            {/* Start Date - End Date Range Picker */}
                            <div className="flex-1 min-w-[300px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Date Range</label>
                                <DatePicker
                                    selected={dateRange[0]}
                                    onChange={handleDateRangeChange}
                                    startDate={dateRange[0]}
                                    endDate={dateRange[1]}
                                    selectsRange
                                    isClearable
                                    placeholderText="Select date range"
                                    className="form-input border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400 w-full transition-all duration-200"
                                    dateFormat="yyyy-MM-dd"
                                />
                            </div>
                        </div>
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Product Batch ID
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Received Date</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Legal Possession
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Authorized Trading Partner
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Received Under DSCSA
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Comment</th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={loading} itemCount={(transactionStatements as any)?.transactionStatements?.content?.length ?? 0} animationKey={`statements-${page}`}>
                                            {(transactionStatements as any)?.transactionStatements?.content?.map(
                                                (item: any, index: number) => (
                                                    <AnimatedTableRow
                                                        rowKey={item?.productBatchId ?? index}
                                                        detailRecord={item}
                                                        className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                    >
                                                        <td className="px-3.5 py-2.5">{item?.productBatchId || `-`}</td>
                                                        <td className="px-3.5 py-2.5">
                                                            {formatDate(item?.receivedDate)}
                                                        </td>
                                                        <td className="px-3.5 py-2.5">
                                                            {formatBoolean(item?.legalPossession)}
                                                        </td>
                                                        <td className="px-3.5 py-2.5">
                                                            {formatBoolean(item?.authorizedTradingPartner)}
                                                        </td>
                                                        <td className="px-3.5 py-2.5">
                                                            {formatBoolean(item?.receivedUnderDSCSA)}
                                                        </td>
                                                        <td className="px-3.5 py-2.5">{item?.comment || `-`}</td>
                                                    </AnimatedTableRow>
                                                )
                                            )}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                                <Pagination
                                    perPageData={
                                        (transactionStatements as any)?.transactionStatements?.pageable?.pageSize || 10
                                    }
                                    total={(transactionStatements as any)?.transactionStatements?.totalElements || 0}
                                    currentPage={page}
                                    changePage={changePage}
                                    length={
                                        (transactionStatements as any)?.transactionStatements?.numberOfElements ||
                                        (transactionStatements as any)?.transactionStatements?.content?.length ||
                                        0
                                    }
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default TransactionStatements;
